// Filesystems — named, shareable file containers for persistent agents.
// Attach one to an agent and its threads gain file tools (read/write/edit/
// list/grep) against it; this page creates/browses the containers themselves.
import { useClient } from '@cortex/react-query';
import type { PersistentAgentFilesystemDetail } from '@cortex/types';
import { fork } from '@cortex/utils/fork';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { EmptyState } from '../../../../components/common/EmptyState';
import { Modal } from '../../../../components/common/Modal';
import { PageHeader } from '../../../../components/common/PageHeader';
import { Spinner } from '../../../../components/common/Spinner';
import { routes } from '../../../../lib/constants';
import { formatRelative } from '../../../../lib/format';

export const Route = createFileRoute('/agents/orchestration/filesystems/')({
  component: FilesystemsPage,
});

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FilesystemsPage() {
  const client = useClient();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const listQuery = useQuery({
    queryKey: ['persistent-agent-filesystems'],
    queryFn: () =>
      client.http
        .get('v1/persistent-agent-filesystems')
        .json<IListEnvelope<PersistentAgentFilesystemDetail>>(),
  });

  const create = useMutation({
    mutationFn: () =>
      client.http
        .post('v1/persistent-agent-filesystems', {
          json: {
            name: name.trim(),
            ...(description.trim() ? { description: description.trim() } : {}),
          },
        })
        .json<PersistentAgentFilesystemDetail>(),
    onSuccess: async (fs) => {
      setShowCreate(false);
      setName('');
      setDescription('');
      await qc.invalidateQueries({ queryKey: ['persistent-agent-filesystems'] });
      fork(navigate({ to: routes.orchestrationFilesystem(fs.id) }));
    },
  });

  const items = listQuery.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="Filesystems"
        actions={
          <button className="btn btn--sm btn--primary" onClick={() => setShowCreate(true)}>
            New filesystem
          </button>
        }
      />

      {listQuery.isLoading && <Spinner />}
      {!listQuery.isLoading && items.length === 0 && (
        <EmptyState
          title="No filesystems yet"
          description="Create one, add a few files (or let an agent write them), then attach it to a persistent agent."
        />
      )}

      {items.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Files</th>
                <th>Size</th>
                <th>Attached to</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {items.map((fs) => (
                <tr
                  key={fs.id}
                  onClick={() => navigate({ to: routes.orchestrationFilesystem(fs.id) })}
                  style={{ cursor: 'pointer' }}
                  title="Browse files"
                >
                  <td>
                    <div style={{ fontWeight: 600 }}>{fs.name}</div>
                    {fs.description && (
                      <div className="t-xs t-muted" style={{ marginTop: 2 }}>
                        {fs.description}
                      </div>
                    )}
                  </td>
                  <td className="t-xs">{fs.fileCount}</td>
                  <td className="t-xs">{formatBytes(fs.totalBytes)}</td>
                  <td className="t-xs">
                    {fs.attachedGroups.length === 0 ? (
                      <span className="t-muted">—</span>
                    ) : (
                      fs.attachedGroups.map((g) => (
                        <span
                          key={g.id}
                          className="badge badge--neutral"
                          style={{ marginRight: 4 }}
                        >
                          {g.name}
                        </span>
                      ))
                    )}
                  </td>
                  <td className="t-xs t-muted">{formatRelative(fs.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={showCreate}
        onClose={() => {
          if (!create.isPending) setShowCreate(false);
        }}
        title="New filesystem"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <button
              className="btn btn--ghost btn--sm"
              disabled={create.isPending}
              onClick={() => setShowCreate(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn--primary btn--sm"
              disabled={create.isPending || name.trim().length === 0}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Creating…' : 'Create'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <label className="t-sm" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            Name
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Fleet notes"
              autoFocus={true}
            />
          </label>
          <label className="t-sm" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            Description <span className="t-muted">(optional)</span>
            <input
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Shared scratch space for the drone fleet"
            />
          </label>
          {create.isError && (
            <div className="alert alert--danger">
              {String((create.error as Error)?.message ?? 'Failed to create filesystem')}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
